import { useRouteState, updateRoute } from './routeState'
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { roomRequest } from './roomApi'
import type { RoomMember, RoomUser, WritingTask } from './types'

type PageRequest = { taskId?: string; tour?: boolean; serial: string }
type TaskState = {
  user: RoomUser
  tasks: WritingTask[]
  members: RoomMember[]
  loaded: boolean
  loading: boolean
  busy: boolean
  error: string
  writingScope: WritingTask['scope']
  setWritingScope: (scope: WritingTask['scope']) => void
  refresh: () => Promise<boolean>
  save: (body: Partial<WritingTask>, task?: WritingTask) => Promise<WritingTask>
  track: (projectId: string, targetId: string, scope: WritingTask['scope'], parentId?: string) => Promise<WritingTask>
  page: PageRequest | null
  openPage: (request?: Omit<PageRequest, 'serial'>) => void
  closePage: () => void
}
const Context = createContext<TaskState | null>(null)
export function useTasks() {
  const value = useContext(Context)
  if (!value) throw new Error('The task workspace is unavailable.')
  return value
}

export function TaskProvider({ user, children }: { user: RoomUser; children: ReactNode }) {
  const [tasks, setTasks] = useState<WritingTask[]>([])
  const [members, setMembers] = useState<RoomMember[]>([])
  const [loaded, setLoaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [writingScope, setWritingScope] = useState<WritingTask['scope']>('team')
  const [pageOpen] = useRouteState<boolean>('tasks', false)
  const [taskId] = useRouteState<string | null>('task', null)
  const [taskTour] = useRouteState<boolean>('task-tour', false)
  const page = pageOpen ? { taskId: taskId || undefined, tour: taskTour, serial: taskId || 'tasks' } : null
  const generation = useRef(0)
  const mutation = useRef(false)
  const refresh = useCallback(async () => {
    if (mutation.current) return false
    const version = ++generation.current
    setLoading(true)
    try {
      const [data, people] = await Promise.all([
        roomRequest<{ tasks: WritingTask[] }>(user.id, '/api/workspace'),
        roomRequest<{ users: RoomMember[] }>(user.id, '/api/users'),
      ])
      if (version !== generation.current) return false
      setTasks(data.tasks)
      setMembers(people.users)
      setLoaded(true)
      setError('')
      return true
    } catch (e) {
      if (version === generation.current) setError(e instanceof Error ? e.message : 'Tasks could not be refreshed.')
      return false
    } finally {
      if (version === generation.current) setLoading(false)
    }
  }, [user.id])
  useEffect(() => {
    void refresh()
    const visible = () => { if (document.visibilityState === 'visible') void refresh() }
    const interval = window.setInterval(visible, 20_000)
    window.addEventListener('focus', visible)
    window.addEventListener('bramble:remote-change', visible)
    window.addEventListener('bramble:writing-changed', visible)
    document.addEventListener('visibilitychange', visible)
    return () => {
      ++generation.current
      window.clearInterval(interval)
      window.removeEventListener('focus', visible)
      window.removeEventListener('bramble:remote-change', visible)
      window.removeEventListener('bramble:writing-changed', visible)
      document.removeEventListener('visibilitychange', visible)
    }
  }, [refresh])
  const change = async (operation: () => Promise<{ task: WritingTask; tasks?: WritingTask[] }>) => {
    if (mutation.current) throw new Error('Wait for the current task change to finish.')
    mutation.current = true
    ++generation.current
    setLoading(false)
    setBusy(true)
    setError('')
    try {
      const result = await operation()
      setTasks(old => result.tasks || [...old.filter(t => t.id !== result.task.id), result.task])
      setLoaded(true)
      window.dispatchEvent(new Event('bramble:tasks-changed'))
      return result.task
    } catch (e) {
      const message = e instanceof Error ? e.message : 'This task could not be saved.'
      setError(message)
      throw e
    } finally {
      mutation.current = false
      setBusy(false)
    }
  }
  const save: TaskState['save'] = (body, task) => change(() => roomRequest(user.id,
    '/api/tasks' + (task ? '/' + task.id : ''), task ? 'PATCH' : 'POST',
    { ...body, ...(task ? { version: task.version } : {}) },
  ))
  const track: TaskState['track'] = (projectId, targetId, scope, parentId) => change(() =>
    roomRequest(user.id, '/api/tasks/track', 'POST', { projectId, targetId, scope, parentId }))
  return <Context.Provider value={{ user, tasks, members, loaded, loading, busy, error, refresh, save, track, writingScope, setWritingScope,
    page, openPage: request => updateRoute({ tasks: '1', task: request?.taskId || null, 'task-tour': request?.tour ? '1' : null, 'task-tour-step': null }), closePage: () => updateRoute({ tasks: null, task: null, 'task-tour': null, 'task-tour-step': null, 'task-edit': null, 'task-organize': null, 'task-organize-scope': null, 'task-parent': null }) }}>
    {children}
  </Context.Provider>
}
